import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
    Droplets, Loader2, CheckCircle, MapPin, Waves,
    Ruler, Palette, FlaskConical, Thermometer, Settings,
    Save, Sparkles, MessageCircle, ArrowRight, Calculator
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const POOL_TYPES = [
    { value: 'enterree', label: 'Enterrée', icon: '🏊' },
    { value: 'hors-sol', label: 'Hors-sol', icon: '⬜' },
    { value: 'spa', label: 'Spa / Jacuzzi', icon: '♨️' }
];

const SHAPES = [
    { value: 'rectangulaire', label: 'Rectangulaire' },
    { value: 'carre', label: 'Carrée' },
    { value: 'ronde', label: 'Ronde' },
    { value: 'ovale', label: 'Ovale' },
    { value: 'haricot', label: 'Haricot / Libre' },
];

const TREATMENTS = [
    { value: 'chlore', label: 'Chlore' },
    { value: 'sel', label: 'Électrolyse au sel' },
    { value: 'brome', label: 'Brome' },
    { value: 'oxygene', label: 'Oxygène actif' },
    { value: 'pah', label: 'PAH (polyhexaméthylène)' },
];

const COATINGS = [
    { value: 'liner', label: 'Liner' },
    { value: 'coque', label: 'Coque polyester' },
    { value: 'beton', label: 'Béton peint' },
    { value: 'carrelage', label: 'Carrelage / Mosaïque' },
    { value: 'pvc-arme', label: 'PVC armé' },
];

const COVERS = [
    { value: 'aucun', label: 'Aucune couverture' },
    { value: 'bache', label: 'Bâche à bulles' },
    { value: 'volet', label: 'Volet roulant' },
    { value: 'abri', label: 'Abri de piscine' },
];

const EMPTY_FORM = {
    location: '',
    pool_type: '',
    shape: '',
    length: '',
    width: '',
    depth_min: '',
    depth_max: '',
    volume: '',
    coating: '',
    treatment_system: '',
    has_heat_pump: false,
    has_cover: 'aucun',
};

export default function PoolConfigPage() {
    const navigate = useNavigate();
    const { user, checkAuth, loading: authLoading } = useAuth();
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasProfile, setHasProfile] = useState(false);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate('/auth');
            return;
        }
        loadProfile();
    }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadProfile = async () => {
        setLoading(true);
        try {
            const profile = await apiRequest('/pool-config/profile');
            if (profile && Object.keys(profile).length > 0) {
                setHasProfile(true);
                setForm({
                    ...EMPTY_FORM,
                    ...profile,
                    length: profile.length ?? '',
                    width: profile.width ?? '',
                    depth_min: profile.depth_min ?? '',
                    depth_max: profile.depth_max ?? '',
                    volume: profile.volume ?? '',
                    has_heat_pump: !!profile.has_heat_pump,
                    has_cover: profile.has_cover || 'aucun',
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    // Auto-calculate volume when dimensions change
    const computedVolume = (() => {
        const l = parseFloat(form.length);
        const w = parseFloat(form.width);
        const dmin = parseFloat(form.depth_min);
        const dmax = parseFloat(form.depth_max);
        if (!l || !w) return null;
        const avg = (dmin && dmax) ? (dmin + dmax) / 2 : (dmax || dmin || 1.5);
        return Math.round(l * w * avg * 10) / 10;
    })();

    const useComputedVolume = () => {
        if (computedVolume) {
            update('volume', computedVolume);
            toast.success(`Volume calculé : ${computedVolume} m³`);
        }
    };

    const handleSave = async () => {
        if (!form.pool_type) {
            toast.error('Indiquez le type de piscine');
            return;
        }
        if (!form.volume && !computedVolume) {
            toast.error('Indiquez le volume ou les dimensions');
            return;
        }
        if (!form.treatment_system) {
            toast.error('Indiquez le type de traitement');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                location: form.location || null,
                pool_type: form.pool_type || null,
                shape: form.shape || null,
                length: form.length ? parseFloat(form.length) : null,
                width: form.width ? parseFloat(form.width) : null,
                depth_min: form.depth_min ? parseFloat(form.depth_min) : null,
                depth_max: form.depth_max ? parseFloat(form.depth_max) : null,
                volume: form.volume ? parseFloat(form.volume) : (computedVolume || null),
                coating: form.coating || null,
                treatment_system: form.treatment_system || null,
                has_heat_pump: form.has_heat_pump,
                has_cover: form.has_cover && form.has_cover !== 'aucun' ? form.has_cover : null,
            };

            await apiRequest('/pool-config/save', {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            await checkAuth();
            toast.success('Profil piscine enregistré !');
            setHasProfile(true);
        } catch (error) {
            console.error(error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const progress = [
        { key: 'pool_type', filled: !!form.pool_type },
        { key: 'volume', filled: !!(form.volume || computedVolume) },
        { key: 'treatment_system', filled: !!form.treatment_system },
        { key: 'coating', filled: !!form.coating },
        { key: 'location', filled: !!form.location },
        { key: 'has_cover', filled: !!(form.has_heat_pump || form.has_cover !== 'aucun') },
    ];
    const progressPct = Math.round((progress.filter((p) => p.filled).length / progress.length) * 100);

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="pool-config-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
                {/* Hero */}
                <div className="text-center">
                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 mb-4">
                        <Settings className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Ma Piscine</h1>
                    <p className="text-muted-foreground">
                        {hasProfile
                            ? 'Modifiez les informations de votre piscine à tout moment'
                            : 'Configurez votre profil pour des recommandations personnalisées'}
                    </p>
                </div>

                {/* Progress bar */}
                <Card className="glass rounded-2xl">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2 text-sm">
                            <span className="font-medium">Complétude</span>
                            <span className="font-semibold text-primary">{progressPct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <div
                                className="h-full bg-primary rounded-full transition-all"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Type de piscine */}
                <Card className="glass rounded-2xl">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Waves className="h-5 w-5 text-primary" />
                            Type de piscine <span className="text-destructive">*</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-3 gap-2">
                            {POOL_TYPES.map((t) => (
                                <button
                                    key={t.value}
                                    type="button"
                                    onClick={() => update('pool_type', t.value)}
                                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                                        form.pool_type === t.value
                                            ? 'border-primary bg-primary/10'
                                            : 'border-border hover:border-primary/40'
                                    }`}
                                    data-testid={`pool-type-${t.value}`}
                                >
                                    <div className="text-2xl mb-1">{t.icon}</div>
                                    <div className="text-sm font-medium">{t.label}</div>
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Dimensions & Volume */}
                <Card className="glass rounded-2xl">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Ruler className="h-5 w-5 text-primary" />
                            Dimensions & Volume <span className="text-destructive">*</span>
                        </CardTitle>
                        <CardDescription>Remplissez les dimensions, le volume sera calculé automatiquement.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Shape */}
                        <div>
                            <Label>Forme</Label>
                            <Select value={form.shape} onValueChange={(v) => update('shape', v)}>
                                <SelectTrigger className="rounded-full mt-1" data-testid="shape-select">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {SHAPES.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Dimensions */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <Label>Longueur (m)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={form.length}
                                    onChange={(e) => update('length', e.target.value)}
                                    placeholder="8"
                                    className="rounded-full mt-1"
                                    data-testid="length-input"
                                />
                            </div>
                            <div>
                                <Label>Largeur (m)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={form.width}
                                    onChange={(e) => update('width', e.target.value)}
                                    placeholder="4"
                                    className="rounded-full mt-1"
                                    data-testid="width-input"
                                />
                            </div>
                            <div>
                                <Label>Prof. min (m)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={form.depth_min}
                                    onChange={(e) => update('depth_min', e.target.value)}
                                    placeholder="1.2"
                                    className="rounded-full mt-1"
                                />
                            </div>
                            <div>
                                <Label>Prof. max (m)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={form.depth_max}
                                    onChange={(e) => update('depth_max', e.target.value)}
                                    placeholder="1.8"
                                    className="rounded-full mt-1"
                                />
                            </div>
                        </div>

                        {/* Volume */}
                        <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                                <div className="flex-1">
                                    <Label>Volume (m³)</Label>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        value={form.volume}
                                        onChange={(e) => update('volume', e.target.value)}
                                        placeholder="Ex: 50"
                                        className="rounded-full mt-1"
                                        data-testid="volume-input"
                                    />
                                </div>
                                {computedVolume && computedVolume !== parseFloat(form.volume) && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={useComputedVolume}
                                        className="rounded-full gap-2"
                                        data-testid="use-computed-volume"
                                    >
                                        <Calculator className="h-4 w-4" />
                                        Utiliser {computedVolume} m³
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Treatment & coating */}
                <div className="grid md:grid-cols-2 gap-6">
                    <Card className="glass rounded-2xl">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <FlaskConical className="h-5 w-5 text-primary" />
                                Traitement <span className="text-destructive">*</span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-2">
                                {TREATMENTS.map((t) => (
                                    <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => update('treatment_system', t.value)}
                                        className={`p-2.5 rounded-xl border-2 text-sm font-medium transition ${
                                            form.treatment_system === t.value
                                                ? 'border-primary bg-primary/10'
                                                : 'border-border hover:border-primary/40'
                                        }`}
                                        data-testid={`treatment-${t.value}`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="glass rounded-2xl">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Palette className="h-5 w-5 text-primary" />
                                Revêtement
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Select value={form.coating} onValueChange={(v) => update('coating', v)}>
                                <SelectTrigger className="rounded-full" data-testid="coating-select">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {COATINGS.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>
                </div>

                {/* Equipment & Location */}
                <Card className="glass rounded-2xl">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Thermometer className="h-5 w-5 text-primary" />
                            Équipements & Localisation
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Ville / Région</Label>
                            <Input
                                value={form.location}
                                onChange={(e) => update('location', e.target.value)}
                                placeholder="Flayosc, Var"
                                className="rounded-full mt-1"
                                data-testid="location-input"
                            />
                        </div>
                        <div>
                            <Label>Couverture</Label>
                            <Select value={form.has_cover} onValueChange={(v) => update('has_cover', v)}>
                                <SelectTrigger className="rounded-full mt-1" data-testid="cover-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {COVERS.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40">
                            <div className="flex items-center gap-3">
                                <Thermometer className="h-5 w-5 text-amber-500" />
                                <div>
                                    <p className="font-medium">Pompe à chaleur</p>
                                    <p className="text-xs text-muted-foreground">Permet d'adapter les recommandations saison par saison</p>
                                </div>
                            </div>
                            <Switch
                                checked={form.has_heat_pump}
                                onCheckedChange={(v) => update('has_heat_pump', v)}
                                data-testid="heat-pump-switch"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Save + Quick Actions */}
                <div className="flex flex-col sm:flex-row gap-3 sticky bottom-4">
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 h-12 rounded-full gap-2 shadow-lg"
                        data-testid="save-btn"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer mon profil
                    </Button>
                    {hasProfile && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/analysis')}
                            className="h-12 rounded-full gap-2"
                        >
                            <Sparkles className="h-4 w-4" />
                            Analyser mon eau
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    )}
                </div>

                {/* Helper: assistant fallback */}
                <Card className="glass rounded-2xl border-dashed">
                    <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <MessageCircle className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 text-sm text-muted-foreground">
                            Besoin d'aide pour identifier vos informations ? Notre chatbot Lia peut vous guider pas à pas.
                        </div>
                        <Button variant="ghost" onClick={() => navigate('/chat')} className="rounded-full">
                            Demander à Lia
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
